import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, Copy, Loader2, MapPin, MessageCircle, Phone, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { TicketForm, toPayload } from "@/components/TicketForm";
import { STATUSES, PriorityBadge, StatusBadge, fmtDateTime, fmtMoney, labelOf, waLink } from "@/components/ticketMeta";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

const StatusStepper = ({ status, onChange, disabled }) => {
  const idx = STATUSES.findIndex((s) => s.value === status);
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="status-stepper">
      {STATUSES.map((s, i) => (
        <button key={s.value} disabled={disabled || i === idx} onClick={() => onChange(s.value)} data-testid={`status-step-${s.value}`}
          className={cn("flex items-center justify-center gap-2 rounded-lg border px-3 py-3 text-sm font-semibold transition-colors",
            i < idx && "border-primary/40 bg-primary/10 text-primary",
            i === idx && "border-primary bg-primary text-primary-foreground shadow-lg shadow-primary/25",
            i > idx && "text-muted-foreground hover:border-primary/50 hover:text-foreground")}>
          {i < idx ? <Check className="h-4 w-4" /> : <span className="font-mono text-xs">{i + 1}</span>}
          {s.label}
        </button>
      ))}
    </div>
  );
};

const QuickActions = ({ t }) => (
  <div className="flex flex-wrap gap-2">
    {t.phone && (
      <>
        <Button asChild variant="outline" size="sm" data-testid="detail-call-button"><a href={`tel:${t.phone}`}><Phone className="h-4 w-4" /> Sună</a></Button>
        <Button asChild variant="outline" size="sm" className="border-green-500/40 text-green-600 dark:text-green-400" data-testid="detail-whatsapp-button">
          <a href={waLink(t.phone)} target="_blank" rel="noreferrer"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
        </Button>
      </>
    )}
    {t.address && (
      <Button asChild variant="outline" size="sm" data-testid="detail-map-button">
        <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(t.address)}`} target="_blank" rel="noreferrer"><MapPin className="h-4 w-4" /> Hartă</a>
      </Button>
    )}
  </div>
);

const buildReport = (t) => [
  `*Tichet ${t.code}* – ${labelOf(STATUSES, t.status)}`,
  `Client: ${t.client_name}`, t.phone && `Telefon: ${t.phone}`, t.address && `Adresă: ${t.address}`,
  t.equipment && `Echipament: ${t.equipment}`, `Problemă: ${t.description}`,
  `Tehnician: ${t.technician_name || "nealocat"}`, t.notes && `Intervenție: ${t.notes}`,
  t.total_cost > 0 && `Cost: piese ${fmtMoney(t.cost_parts)} + manoperă ${fmtMoney(t.cost_labor)} = ${fmtMoney(t.total_cost)}`,
].filter(Boolean).join("\n");

const DeleteButton = ({ onConfirm }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <Button variant="ghost" className="text-destructive hover:bg-destructive/10 hover:text-destructive" data-testid="delete-ticket-button"><Trash2 className="h-4 w-4" /> Șterge</Button>
    </AlertDialogTrigger>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Ștergi tichetul?</AlertDialogTitle>
        <AlertDialogDescription>Acțiunea nu poate fi anulată.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="delete-cancel-button">Renunță</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="delete-confirm-button">Șterge</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export default function TicketDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [t, setT] = useState(null);
  const [form, setForm] = useState(null);
  const [techs, setTechs] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get(`/tickets/${id}`).then((r) => { setT(r.data); setForm(r.data); }), [id]);

  useEffect(() => {
    load().catch(() => { toast.error("Tichetul nu a fost găsit"); nav("/"); });
    api.get("/users").then((r) => setTechs(r.data));
  }, [load, nav]);

  const changeStatus = async (status) => {
    try {
      const { data } = await api.put(`/tickets/${id}`, { status });
      setT(data);
      setForm((f) => ({ ...f, status: data.status }));
      toast.success(`Status: ${labelOf(STATUSES, status)}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { client_id, ...rest } = toPayload(form);
      const { data } = await api.put(`/tickets/${id}`, rest);
      setT(data); setForm(data);
      toast.success("Modificările au fost salvate");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const copyReport = async () => {
    try {
      await navigator.clipboard.writeText(buildReport(t));
      toast.success("Raport copiat — lipește-l în WhatsApp");
    } catch (e) { toast.error("Nu am putut copia raportul"); }
  };

  const remove = async () => {
    try { await api.delete(`/tickets/${id}`); toast.success("Tichet șters"); nav("/"); } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!t || !form) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div data-testid="ticket-detail-view" className="animate-in fade-in duration-300">
      <button onClick={() => nav(-1)} className="mb-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="back-button"><ArrowLeft className="h-4 w-4" /> Înapoi</button>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm text-muted-foreground" data-testid="detail-ticket-code">{t.code}</span>
            <StatusBadge status={t.status} data-testid="detail-status-badge" />
            <PriorityBadge priority={t.priority} />
            {t.source === "whatsapp" && <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400"><MessageCircle className="h-3.5 w-3.5" /> din WhatsApp</span>}
          </div>
          <h1 className="mt-2 text-3xl font-extrabold tracking-tight sm:text-4xl" data-testid="detail-client-name">
            {t.client_id ? <Link to={`/clienti/${t.client_id}`} className="hover:text-primary">{t.client_name}</Link> : t.client_name}
          </h1>
          <p className="mt-1 text-xs text-muted-foreground">Creat {fmtDateTime(t.created_at)} de {t.created_by || "—"} · Actualizat {fmtDateTime(t.updated_at)}</p>
        </div>
        <QuickActions t={t} />
      </div>

      <div className="mb-6"><StatusStepper status={t.status} onChange={changeStatus} /></div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-xl border bg-card p-5 sm:p-6 lg:col-span-2">
          <TicketForm value={form} onChange={setForm} technicians={techs} showStatus={false} />
          <div className="mt-8 flex flex-col-reverse gap-2 border-t pt-6 sm:flex-row sm:justify-between">
            {user.role === "admin" ? <DeleteButton onConfirm={remove} /> : <span />}
            <Button onClick={save} disabled={saving} className="h-11 px-6 font-semibold" data-testid="save-changes-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salvează modificările
            </Button>
          </div>
        </div>
        <div className="space-y-4">
          <div className="rounded-xl border bg-card p-5">
            <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Cost total</div>
            <div className="mt-1 font-display text-3xl font-bold" data-testid="detail-total-cost">{fmtMoney(t.total_cost)}</div>
            <div className="mt-1 text-xs text-muted-foreground">Piese {fmtMoney(t.cost_parts)} · Manoperă {fmtMoney(t.cost_labor)}</div>
            <Button variant="outline" className="mt-4 w-full" onClick={copyReport} data-testid="copy-report-button"><Copy className="h-4 w-4" /> Copiază raport pentru WhatsApp</Button>
          </div>
          {t.raw_message && (
            <div className="rounded-xl border border-green-500/30 bg-green-500/5 p-5 dark:bg-emerald-950/40" data-testid="raw-message-box">
              <div className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-green-600 dark:text-green-400"><MessageCircle className="h-3.5 w-3.5" /> Mesaj original</div>
              <p className="max-h-72 overflow-auto whitespace-pre-wrap font-mono text-xs text-muted-foreground">{t.raw_message}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
